// Open Jump Hours Configuration
// Centralized configuration for all Open Jump hours (regular and special)

const LOCATIONS = {
  DEFOREST: 'deforest',
  COTTAGE_GROVE: 'cottageGrove',
}

// The client's schedule and price change takes effect on this date.
// CLEANUP: once this date has passed, delete RATE_CHANGE_DATE,
// REGULAR_HOURS_CURRENT, OPEN_JUMP_PRICE_CURRENT and isAfterRateChange(),
// then rename the *_FROM_SEPT_8 values back to REGULAR_HOURS / OPEN_JUMP_PRICE.
const RATE_CHANGE_DATE = new Date('September 8, 2026')

// Regular weekly hours for each location, through September 7, 2026
const REGULAR_HOURS_CURRENT = {
  [LOCATIONS.DEFOREST]: {
    name: 'DeForest',
    byDay: {
      Tuesday: '12pm-1pm',
      Thursday: '12pm-1pm',
      Friday: '7:30pm-8:30pm',
      Saturday: '12pm-1pm',
    },
  },
  [LOCATIONS.COTTAGE_GROVE]: {
    name: 'Cottage Grove',
    byDay: {
      Tuesday: '10am-12pm',
      Thursday: '10am-12pm',
      Friday: '10am-12pm & 6pm-8pm',
      Saturday: '10am-12pm',
    },
  },
}

// Regular weekly hours from September 8, 2026 onward.
// Only DeForest changed; Cottage Grove stays the same.
const REGULAR_HOURS_FROM_SEPT_8 = {
  [LOCATIONS.DEFOREST]: {
    name: 'DeForest',
    byDay: {
      Tuesday: '12pm-2pm',
      Thursday: '12pm-2pm',
      Friday: '7:30pm-9pm',
      Saturday: '12pm-2pm',
    },
  },
  [LOCATIONS.COTTAGE_GROVE]: {
    name: 'Cottage Grove',
    byDay: {
      Tuesday: '10am-12pm',
      Thursday: '10am-12pm',
      Friday: '10am-12pm & 6pm-8pm',
      Saturday: '10am-12pm',
    },
  },
}

const OPEN_JUMP_PRICE_CURRENT = '$10/child'
const OPEN_JUMP_PRICE_FROM_SEPT_8 = '$15/child'

// Special/Holiday hours with date ranges
// Add new entries here for future special hours periods
const SPECIAL_HOURS = [
  // Example format:
  // {
  //   name: 'Holiday Break 2025-2026',
  //   startDate: new Date('December 20, 2025'),
  //   endDate: new Date('January 3, 2026'),
  //   showModal: true, // Set to true to trigger modal display
  //   hours: {
  //     [LOCATIONS.COTTAGE_GROVE]: [
  //       { date: '12/23', day: 'Mon', times: 'Regular classes and OJ' },
  //       { date: '12/24', day: 'Tue', times: 'Closed' },
  //       ...
  //     ],
  //     [LOCATIONS.DEFOREST]: [
  //       { date: '12/23', day: 'Mon', times: 'Regular classes and OJ' },
  //       { date: '12/24', day: 'Tue', times: 'Closed' },
  //       ...
  //     ],
  //   },
  // },
]

/**
 * Check whether a given moment falls on or after the Sept 8, 2026 change
 * @param {Date} [now] - Moment to test. Defaults to before the change so that
 *   build-time (static HTML) output stays deterministic; the client corrects it.
 * @returns {boolean} True once the new hours and price are in effect
 */
function isAfterRateChange(now) {
  if (!now) return false
  return now.getTime() >= RATE_CHANGE_DATE.getTime()
}

/**
 * Get the Open Jump price for a given moment
 * @param {Date} [now] - Moment to price. Omit for the pre-change price.
 * @returns {string} Price string, e.g. '$15/child'
 */
function getOpenJumpPrice(now) {
  return isAfterRateChange(now)
    ? OPEN_JUMP_PRICE_FROM_SEPT_8
    : OPEN_JUMP_PRICE_CURRENT
}

/**
 * Get the regular weekly hours in effect at a given moment
 * @param {Date} [now] - Moment to look up. Omit for the pre-change hours.
 * @returns {Object} Hours keyed by location
 */
function getRegularHours(now) {
  return isAfterRateChange(now)
    ? REGULAR_HOURS_FROM_SEPT_8
    : REGULAR_HOURS_CURRENT
}

/**
 * Get the currently active special hours period (if any)
 * @param {Date} [now] - Moment to test. Defaults to the current time.
 * @returns {Object|null} Active special hours object or null
 */
function getActiveSpecialHours(now) {
  const at = (now || new Date()).getTime()
  return (
    SPECIAL_HOURS.find(special => {
      return (
        (!special.startDate || special.startDate.getTime() <= at) &&
        special.endDate.getTime() >= at
      )
    }) || null
  )
}

/**
 * Check if the modal should be displayed
 * @param {Date} [now] - Moment to test. Defaults to the current time.
 * @returns {boolean} True if modal should show
 */
function shouldShowModal(now) {
  const activeSpecial = getActiveSpecialHours(now)
  return activeSpecial && activeSpecial.showModal
}

/**
 * Get the hours to display for a given location
 * Returns special hours if active, otherwise returns regular hours
 * @param {string} location - Location constant (LOCATIONS.DEFOREST or LOCATIONS.COTTAGE_GROVE)
 * @param {Date} [now] - Moment to look up. Omit for build-safe defaults.
 * @returns {Object} Object with type ('special' or 'regular') and data
 */
function getDisplayHours(location, now) {
  // Without an explicit moment this returns the build-safe baseline (no
  // special hours, pre-change regular hours) so server and client agree.
  const activeSpecial = now ? getActiveSpecialHours(now) : null
  if (activeSpecial) {
    return {
      type: 'special',
      data: activeSpecial.hours[location],
      name: activeSpecial.name,
    }
  }
  return {
    type: 'regular',
    data: getRegularHours(now)[location],
  }
}

export {
  LOCATIONS,
  RATE_CHANGE_DATE,
  REGULAR_HOURS_CURRENT,
  REGULAR_HOURS_FROM_SEPT_8,
  OPEN_JUMP_PRICE_CURRENT,
  OPEN_JUMP_PRICE_FROM_SEPT_8,
  SPECIAL_HOURS,
  isAfterRateChange,
  getOpenJumpPrice,
  getRegularHours,
  getActiveSpecialHours,
  shouldShowModal,
  getDisplayHours,
}
